import React from "react";
import {
  Grid,
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";

import StoreApi from "../../services/Apis/Store.Api";
import SemiCircleProgress from "../../components/Chart";
import { NpsGaugeCard } from "../../components/NpsGaugeWidget";
import { isFordStore, isKiaStore } from "../../utils/storeTypes";
import { calculateNps } from "../../utils/nps";

class Dashboard extends React.Component {
  constructor(props) {
    super(props);
    const currentMonthIndex = new Date().getMonth();
    const currentMonth = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ][currentMonthIndex];

    this.state = {
      storesList: [],
      selectedStore: {},
      selectedMonth: currentMonth,
      sales: "",
      salesChange: "",
      service: "",
      serviceChange: "",
      csi: "",
      salesPromoters: "",
      salesPassives: "",
      salesDetractors: "",
      servicePromoters: "",
      servicePassives: "",
      serviceDetractors: "",
    };
  }

  componentDidMount() {
    this.fetchStores();
  }

  getScoreStateFromStore = (store) => ({
    sales: store?.sales ?? "",
    salesChange: store?.salesChange ?? "",
    service: store?.service ?? "",
    serviceChange: store?.serviceChange ?? "",
    csi: store?.csi ?? "",
    salesPromoters: store?.salesPromoters ?? "",
    salesPassives: store?.salesPassives ?? "",
    salesDetractors: store?.salesDetractors ?? "",
    servicePromoters: store?.servicePromoters ?? "",
    servicePassives: store?.servicePassives ?? "",
    serviceDetractors: store?.serviceDetractors ?? "",
  });

  fetchStores = async (preserveStoreId) => {
    try {
      const response = await StoreApi.getStores();
      const selectedStore = preserveStoreId
        ? response?.find((s) => s.ID === preserveStoreId) ?? response?.[0] ?? {}
        : response?.[0] ?? {};

      this.setState({
        storesList: response ?? [],
        selectedStore,
        ...this.getScoreStateFromStore(selectedStore),
      });
    } catch (error) {
      console.error("Error fetching stores", error);
    }
  };

  handleStoreChange = (event, value) => {
    const nextStore = value ?? {};
    this.setState({
      selectedStore: nextStore,
      ...this.getScoreStateFromStore(nextStore),
    });
  };

  handleInputChange = (event) => {
    const { name, value } = event.target;
    const store = this.state.selectedStore;
    const isKia = isKiaStore(store);
    const isFord = isFordStore(store);

    if (isFord) {
      if (value !== "" && (Number(value) < 0 || !Number.isInteger(Number(value)))) {
        return alert("Counts must be whole numbers 0 or greater");
      }
      this.setState({ [name]: value });
      return;
    }

    if (isKia && value > 100) return alert("maximum input can be 100");
    if (
      !isKia &&
      (name === "sales" || name === "service") &&
      value > 5
    )
      return alert("maximum input can be 5");
    if (
      !isKia &&
      (name === "sales" || name === "service") &&
      value < 0
    )
      return alert("minimum input can be 0.1");
    this.setState({ [name]: value });
  };

  getFordNpsValues = () => {
    const salesNps = calculateNps(
      this.state.salesPromoters,
      this.state.salesPassives,
      this.state.salesDetractors
    );
    const serviceNps = calculateNps(
      this.state.servicePromoters,
      this.state.servicePassives,
      this.state.serviceDetractors
    );
    return { salesNps, serviceNps };
  };

  handleSave = async () => {
    try {
      const store = this.state.selectedStore;
      const isKia = isKiaStore(store);
      const isFord = isFordStore(store);
      const storeID = store.ID;

      if (!storeID) return alert("Store cannot be empty");

      let body = { storeID };

      if (isFord) {
        const {
          salesPromoters,
          salesPassives,
          salesDetractors,
          servicePromoters,
          servicePassives,
          serviceDetractors,
        } = this.state;

        const salesTotal =
          Number(salesPromoters) + Number(salesPassives) + Number(salesDetractors);
        const serviceTotal =
          Number(servicePromoters) +
          Number(servicePassives) +
          Number(serviceDetractors);

        if (salesTotal === 0) return alert("Sales response counts cannot all be zero");
        if (serviceTotal === 0)
          return alert("Service response counts cannot all be zero");

        const { salesNps, serviceNps } = this.getFordNpsValues();

        body = {
          ...body,
          sales: salesNps,
          service: serviceNps,
          salesPromoters,
          salesPassives,
          salesDetractors,
          servicePromoters,
          servicePassives,
          serviceDetractors,
        };
      } else {
        const { sales, salesChange, serviceChange, service, csi } = this.state;
        body = { ...body, sales, salesChange, service, serviceChange, csi };

        if (isKia) {
          if (sales === "") return alert("Sales Index cannot be empty");
          if (service === "") return alert("Service Index cannot be empty");
          if (csi === "") return alert("CSI cannot be empty");
        } else {
          if (sales === "") return alert("Sales cannot be empty");
          if (service === "") return alert("Service cannot be empty");
          if (salesChange === "") return alert("Sales Change cannot be empty");
          if (serviceChange === "") return alert("Service Change cannot be empty");
        }
      }

      this.setState({ saving: true });
      await StoreApi.updateStoreScore(body, storeID);
      toast.success("Saved Successfully");

      this.setState({ saving: false });
      await this.fetchStores(storeID);
    } catch (error) {
      this.setState({ saving: false });
      console.error("Error updating stores", error);
    }
  };

  renderFordColumn = (label, prefix, npsValue, changeValue) => (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {label} NPS Responses
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Promoters (4-5)"
            type="number"
            name={`${prefix}Promoters`}
            value={this.state[`${prefix}Promoters`]}
            onChange={this.handleInputChange}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Passives (3)"
            type="number"
            name={`${prefix}Passives`}
            value={this.state[`${prefix}Passives`]}
            onChange={this.handleInputChange}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Detractors (1-2)"
            type="number"
            name={`${prefix}Detractors`}
            value={this.state[`${prefix}Detractors`]}
            onChange={this.handleInputChange}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <NpsGaugeCard
            title={`${label} NPS`}
            value={npsValue}
            delta={changeValue}
            promoters={this.state[`${prefix}Promoters`]}
            passives={this.state[`${prefix}Passives`]}
            detractors={this.state[`${prefix}Detractors`]}
            periodLabel={this.state.selectedMonth}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  renderFordInputs = () => {
    const { salesNps, serviceNps } = this.getFordNpsValues();
    return (
      <>
        {this.renderFordColumn(
          "Sales",
          "sales",
          salesNps,
          this.state.salesChange
        )}
        {this.renderFordColumn(
          "Service",
          "service",
          serviceNps,
          this.state.serviceChange
        )}
      </>
    );
  };

  renderStandardInputs = (isKia) => (
    <>
      <Grid item xs={8} sm={2}>
        <TextField
          fullWidth
          label={isKia ? "SSI (Sales Index)" : "Sales"}
          type="number"
          name="sales"
          value={this.state.sales}
          onChange={this.handleInputChange}
          variant="outlined"
        />
      </Grid>
      {!isKia && (
        <Grid item xs={4} sm={2}>
          <TextField
            fullWidth
            label="Sales Change"
            type="number"
            name="salesChange"
            value={this.state.salesChange}
            onChange={this.handleInputChange}
            variant="outlined"
          />
        </Grid>
      )}
      <Grid item xs={8} sm={2}>
        <TextField
          fullWidth
          label={isKia ? "ASSI (Service Index)" : "Service"}
          type="number"
          name="service"
          value={this.state.service}
          onChange={this.handleInputChange}
          variant="outlined"
        />
      </Grid>
      {!isKia && (
        <Grid item xs={4} sm={2}>
          <TextField
            fullWidth
            label="Service Change"
            type="number"
            name="serviceChange"
            value={this.state.serviceChange}
            onChange={this.handleInputChange}
            variant="outlined"
          />
        </Grid>
      )}
      {isKia && (
        <Grid item xs={12} sm={2}>
          <TextField
            fullWidth
            label="CSI (Combined Index)"
            type="number"
            name="csi"
            value={this.state.csi}
            onChange={this.handleInputChange}
            variant="outlined"
          />
        </Grid>
      )}
    </>
  );

  render() {
    const store = this.state.selectedStore;
    const isKia = isKiaStore(store);
    const isFord = isFordStore(store);

    return (
      <>
        <Grid
          container
          spacing={2}
          style={{ padding: 20 }}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Grid item xs={12} sm={"auto"}>
            <Autocomplete
              options={this.state.storesList}
              getOptionLabel={(option) => option.name}
              style={{ minWidth: 250 }}
              onChange={this.handleStoreChange}
              value={this.state.selectedStore}
              renderInput={(params) => (
                <TextField
                  fullWidth
                  {...params}
                  label="Select Store"
                  variant="outlined"
                  helperText={`Last Update: ${moment(
                    this.state.selectedStore.lastUpdate
                  ).format("YYYY-MM-DD")}`}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          style={{ padding: 20 }}
          justifyContent={"center"}
          alignItems={"center"}
        >
          {isFord
            ? this.renderFordInputs()
            : this.renderStandardInputs(isKia)}
        </Grid>
        {!isFord && (
          <Grid
            container
            justifyContent={"center"}
            alignItems={"flex-start"}
            sx={{ my: 4 }}
          >
            <Grid item>
              <SemiCircleProgress
                label={isKia ? "SSI (Sales Index)" : "Sales"}
                size={75}
                maxValue={isKia ? 100 : 5}
                value={this.state.sales}
                delta={this.state.salesChange}
              />
            </Grid>
            <Grid item>
              <SemiCircleProgress
                label={isKia ? "ASSI (Service Index)" : "Service"}
                size={75}
                maxValue={isKia ? 100 : 5}
                value={this.state.service}
                delta={this.state.serviceChange}
              />
            </Grid>
            {isKia && (
              <SemiCircleProgress
                label="CSI (Combined Index)"
                size={75}
                maxValue={100}
                value={this.state.csi}
                delta={""}
              />
            )}
          </Grid>
        )}
        <Grid
          container
          spacing={2}
          style={{ padding: 20 }}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Grid item xs={12} sm={3}>
            {this.state.saving ? (
              <CircularProgress />
            ) : (
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={this.handleSave}
              >
                Save
              </Button>
            )}
          </Grid>
        </Grid>
      </>
    );
  }
}

export default Dashboard;
